<script setup>
import { onMounted, ref, inject, computed } from "vue";
import { router, usePage, Link } from "@inertiajs/vue3";
const page = usePage();

const locale = computed(() => {
    return page.props.locale;
});

// Translation helper function
const __t = inject("__t");

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: ''
    },
});
const staticMenuItems = computed(() => [
    {
        label: { ar: __t('home'), en: __t('home'), ru: __t('home'), tr: __t('home'), es: __t('home'), fr: __t('home') },
        url: 'home',
        child: []
    },
    {
        label: { ar: __t('about_us'), en: __t('about_us'), ru: __t('about_us'), tr: __t('about_us'), es: __t('about_us'), fr: __t('about_us') },
        url: 'about',
        child: []
    },
    {
        label: { ar: __t('products'), en: __t('products'), ru: __t('products'), tr: __t('products'), es: __t('products'), fr: __t('products') },
        url: 'products',
        child: []
    },
    {
        label: { ar: __t('Projects'), en: __t('Projects'), ru: __t('Projects'), tr: __t('Projects'), es: __t('Projects'), fr: __t('Projects') },
        url: 'projects',
        child: []
    },
    {
        label: { ar: __t('events'), en: __t('events'), ru: __t('events'), tr: __t('events'), es: __t('events'), fr: __t('events') },
        url: 'events',
        child: []
    },

    {
        label: { ar: __t('iso-certifications'), en: __t('iso-certifications'), ru: __t('iso-certifications'), tr: __t('iso-certifications'), es: __t('iso-certifications'), fr: __t('iso-certifications') },
        url: 'iso-certifications',
        child: []
    },

    {
        label: { ar: __t('News'), en: __t('News'), ru: __t('News'), tr: __t('News'), es: __t('News'), fr: __t('News') },
        url: 'blog',
        child: []
    },
    {
        label: { ar: __t('contact_us'), en: __t('contact_us'), ru: __t('contact_us'), tr: __t('contact_us'), es: __t('contact_us'), fr: __t('contact_us') },
        url: 'contact',
        child: []
    },
    {
        label: { ar: __t('careers'), en: __t('careers'), ru: __t('careers'), tr: __t('careers'), es: __t('careers'), fr: __t('careers') },
        url: 'careers',
        child: []
    },
]);

const navMenu = ref([]);
const menuStates = ref({});
const expandedGroups = ref([]);

const loading = ref(true);
onMounted(() => {
    if (props.id) {
        axios
            .get(route("api.menu", props.id))

            .then((data) => {

                navMenu.value = data.data.menu.items;
                navMenu.value.forEach((item, index) => {
                    menuStates.value[index] = false; // Initially, all menus are closed
                });
            })
            .catch((error) => { })
            .finally(() => {
                loading.value = false;
            });
    } else {
        loading.value = false;
    }
});

// Function to check if a URL is internal
const isInternalUrl = (url) => {
    if (!url) return false;
    if (route().has(url)) return true;

    const appUrl = import.meta.env.VITE_APP_URL;
    if (!appUrl) return false;

    // Handle URLs that might be route names or direct URLs
    if (url.includes('|') && url.split('|')[1]) {
        return true; // Posts are internal
    }

    // Check if the URL starts with our domain
    if (typeof url === 'string') {
        return url.startsWith(appUrl) || url.startsWith('/');
    }

    return false;
};

// Function to get URL from item
const getUrl = (item) => {
    if (item.func === 'post' && item.url.split('|')[1]) {
        return route('post.one', (item.url.split('|')[1]));
    } else if (route().has(item.url)) {
        return route(item.url);
    }
    return item.url;
};
</script>
<template>
    <div class="flex flex-col items-start justify-start w-full h-full gap-1">
        <v-list v-model:opened="expandedGroups" class="w-full py-0" bg-color="transparent">
            <!-- Static Menu Items -->
            <template v-for="(item, index) in staticMenuItems" :key="'static-' + index">
                <!-- Menu item without children -->
                <v-list-item v-if="item.child.length == 0" class="w-full px-2">
                    <Link v-if="item.url !== '#'" :href="route(item.url)" class="block w-full">
                        <div class="text-black dark:text-white text-[15px] py-2">
                            {{ item.label[locale] }}
                        </div>
                    </Link>
                </v-list-item>

                <!-- Menu item with children (expandable) -->
                <v-list-group v-else :value="'static-' + index">
                    <template v-slot:activator="{ props }">
                        <v-list-item v-bind="props" class="px-2">
                            <div class="text-black dark:text-white text-[15px]">
                                {{ item.label[locale] }}
                            </div>
                        </v-list-item>
                    </template>

                    <v-list-item v-for="(child, childIndex) in item.child" :key="'static-child-' + childIndex"
                        class="ps-8">
                        <Link :href="route(child.url)" class="block w-full">
                            <div class="text-black dark:text-white text-[14px] py-1">
                                {{ child.label[locale] }}
                            </div>
                        </Link>
                    </v-list-item>
                </v-list-group>
            </template>

            <!-- Divider between static and dynamic menus -->
            <v-divider v-if="navMenu.length > 0" class="my-2"></v-divider>

            <!-- Dynamic Menu Items -->
            <template v-for="(item, index) in navMenu" :key="'dynamic-' + index">
                <!-- Dynamic Item without children -->
                <v-list-item v-if="!item.child || item.child.length == 0" class="w-full px-2">
                    <Link v-if="isInternalUrl(item.url)" :href="getUrl(item)" class="block w-full">
                        <div class="text-black dark:text-white text-[15px] py-2">
                            {{ item.lable[locale] }}
                        </div>
                    </Link>
                    <a v-else :href="getUrl(item)" class="block w-full">
                        <div class="text-black dark:text-white text-[15px] py-2">
                            {{ item.lable[locale] }}
                        </div>
                    </a>
                </v-list-item>

                <!-- Dynamic Item with children -->
                <v-list-group v-else :value="'dynamic-' + index">
                    <template v-slot:activator="{ props }">
                        <v-list-item v-bind="props" class="px-2">
                            <div class="text-black dark:text-white text-[15px]">
                                {{ item.lable[locale] }}
                            </div>
                        </v-list-item>
                    </template>

                    <v-list-item v-for="(child, childIndex) in item.child" :key="'dynamic-child-' + childIndex"
                        class="ps-8">
                        <Link v-if="isInternalUrl(child.url)" :href="getUrl(child)" class="block w-full">
                            <div class="text-black dark:text-white text-[14px] py-1">
                                {{ child.lable[locale] }}
                            </div>
                        </Link>
                        <a v-else :href="getUrl(child)" class="block w-full">
                            <div class="text-black dark:text-white text-[14px] py-1">
                                {{ child.lable[locale] }}
                            </div>
                        </a>
                    </v-list-item>
                </v-list-group>
            </template>
        </v-list>

        <!-- Loading State -->
        <div class="flex flex-col w-full" v-if="loading">
            <div class="flex items-center h-full" v-for="item in 4" :key="item">
                <div class="flex items-center w-full -mx-2 bg-transparent">
                    <v-skeleton-loader :loading="true" class="w-full p-0 m-0 bg-transparent" type="button">
                        <v-list-item class="w-full" lines="tow" subtitle="Subtitle" title="Title" rounded></v-list-item>
                    </v-skeleton-loader>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
:deep(.v-skeleton-loader__button) {
    border-radius: 4px;
    height: 36px;
    margin: 1px;
    max-width: 100%;
}
</style>
